import React, { useState, useEffect, useRef } from "react";
import API from "../../Services/api";
import Header from "../Header";
import Footer from "../Footer";

const Settings = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    username: "",
    mobileNumber: "",
    address: "",
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await API.get("/users/profile");
        setUser(data);
        setForm({
          username: data.username || "",
          mobileNumber: data.mobileNumber || "",
          address: data.address || "",
        });
        setPreviewImage(data.profilePicture || null);
      } catch {
        setError("Failed to load profile. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setSuccess("");
    setError("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setPreviewImage(URL.createObjectURL(file));
    setSuccess("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess("");
    setError("");
    try {
      const formData = new FormData();
      formData.append("username", form.username.trim());
      formData.append("mobileNumber", form.mobileNumber.replace(/\D/g, ""));
      formData.append("address", form.address.trim());
      if (imageFile) formData.append("profilePicture", imageFile);

      await API.put("/users/profile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess("Your profile has been updated successfully!");
      setImageFile(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error;
      setError(msg || "Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F8FAFB]">
      {isLoading ? (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-white bg-opacity-75">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#29752B]"></div>
        </div>
      ) : (
        <>
          <Header isDashboard={true} user={user} isAuthenticated={true} />
          <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-grow max-w-2xl">
            <div className="mb-8">
              <a href="/profile" className="text-[#29752B] text-sm hover:underline flex items-center gap-1">
                ← Back to Dashboard
              </a>
            </div>

            <div className="bg-white rounded-3xl shadow-md p-8">
              <h1 className="text-2xl font-bold text-[#172A3A] mb-2">Profile Settings</h1>
              <p className="text-[#6C757D] text-sm mb-8">Update your personal details below.</p>

              {success && (
                <div className="bg-[#EEFAF7] border border-[#29752B] text-[#18421B] px-4 py-3 rounded-xl mb-6 text-sm">
                  ✓ {success}
                </div>
              )}
              {error && (
                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Profile Picture */}
                <div className="flex items-center space-x-6">
                  <div
                    className="w-20 h-20 rounded-full overflow-hidden bg-[#A7D3BF] flex items-center justify-center text-[#18421B] text-3xl cursor-pointer ring-4 ring-[#EEFAF7] hover:ring-[#29752B] transition"
                    onClick={() => fileInputRef.current.click()}
                  >
                    {previewImage ? (
                      <img src={previewImage} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      user?.username?.charAt(0)?.toUpperCase() || "?"
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      className="text-sm text-[#29752B] font-medium hover:underline"
                    >
                      Change photo
                    </button>
                    <p className="text-xs text-gray-400 mt-1">JPG or PNG, max 5MB</p>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Email (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-[#344054] mb-1">Email address</label>
                  <input
                    type="email"
                    value={user?.email || ""}
                    disabled
                    className="w-full border border-gray-200 bg-gray-50 text-gray-400 px-4 py-2.5 rounded-xl text-sm cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-400 mt-1">Email cannot be changed.</p>
                </div>

                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium text-[#344054] mb-1">Display name</label>
                  <input
                    type="text"
                    name="username"
                    value={form.username}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className="w-full border border-gray-300 px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#29752B]"
                  />
                </div>

                {/* Mobile */}
                <div>
                  <label className="block text-sm font-medium text-[#344054] mb-1">Mobile number</label>
                  <input
                    type="tel"
                    name="mobileNumber"
                    value={form.mobileNumber}
                    onChange={handleChange}
                    placeholder="e.g. 07700900123"
                    className="w-full border border-gray-300 px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#29752B]"
                  />
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-[#344054] mb-1">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Your address"
                    className="w-full border border-gray-300 px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#29752B]"
                  />
                </div>

                <div className="flex items-center space-x-4 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-[#29752B] text-white px-8 py-2.5 rounded-xl text-sm font-medium hover:bg-[#18421B] transition disabled:opacity-60"
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                  <a href="/profile" className="text-sm text-gray-500 hover:text-gray-700">
                    Cancel
                  </a>
                </div>
              </form>
            </div>

            {/* Danger Zone */}
            <div className="bg-white rounded-3xl shadow-md p-8 mt-6 border border-red-100">
              <h2 className="text-base font-bold text-red-600 mb-2">Account</h2>
              <p className="text-sm text-gray-500 mb-4">
                Log out from your account on this device.
              </p>
              <button
                onClick={() => {
                  localStorage.removeItem("token");
                  window.location.href = "/login";
                }}
                className="bg-white border border-red-300 text-red-600 px-6 py-2 rounded-xl text-sm hover:bg-red-50 transition"
              >
                Log out
              </button>
            </div>
          </main>
          <Footer />
        </>
      )}
    </div>
  );
};

export default Settings;
